import { useCallback, useEffect, useRef, useState } from 'react';

const SCROLL_EDGE_TOLERANCE = 2;

export function useHomeHorizontalScrollNav(contentKey?: string | number) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [isPrevDisabled, setIsPrevDisabled] = useState(true);
  const [isNextDisabled, setIsNextDisabled] = useState(true);

  const updateScrollButtons = useCallback(() => {
    const container = scrollRef.current;

    if (!container) {
      setIsPrevDisabled(true);
      setIsNextDisabled(true);
      return;
    }

    const { scrollLeft, scrollWidth, clientWidth } = container;

    setIsPrevDisabled(scrollLeft <= SCROLL_EDGE_TOLERANCE);
    setIsNextDisabled(scrollLeft + clientWidth >= scrollWidth - SCROLL_EDGE_TOLERANCE);
  }, []);

  useEffect(() => {
    const container = scrollRef.current;

    if (!container) {
      return;
    }

    updateScrollButtons();

    container.addEventListener('scroll', updateScrollButtons, { passive: true });

    const resizeObserver = new ResizeObserver(updateScrollButtons);
    resizeObserver.observe(container);

    const content = container.firstElementChild;

    if (content) {
      resizeObserver.observe(content);
    }

    return () => {
      container.removeEventListener('scroll', updateScrollButtons);
      resizeObserver.disconnect();
    };
  }, [contentKey, updateScrollButtons]);

  const scrollToDirection = useCallback((direction: 'prev' | 'next') => {
    const container = scrollRef.current;

    if (!container) {
      return;
    }

    const scrollAmount = Math.max(container.clientWidth * 0.85, 200);

    container.scrollBy({
      left: direction === 'next' ? scrollAmount : -scrollAmount,
      behavior: 'smooth',
    });
  }, []);

  const onPrev = useCallback(() => scrollToDirection('prev'), [scrollToDirection]);
  const onNext = useCallback(() => scrollToDirection('next'), [scrollToDirection]);

  return {
    scrollRef,
    onPrev,
    onNext,
    isPrevDisabled,
    isNextDisabled,
  };
}
